import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import * as XLSX from 'xlsx';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import { IconButton } from '@mui/material';
import Select from 'react-select';
import { ypography } from '@mui/material';
import { tokenIsExpired } from '../../../authUtils';

//const API_URL = 'https://localhost:7291/api/EngineerExpenseApi';
const API_URL = 'https://pp-api.off-ease.com/api/EngineerExpenseApi';
function EngineerExpense() {
    const [dataItem, setDataItem] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();

    }, [isAuthenticated, companyId, token, logout]);

    useEffect(() => {
        // Calculate total income and expense amounts whenever dataItem changes //
        let incomeTotal = 0;
        let expenseTotal = 0;
        dataItem.forEach(item => {
            incomeTotal += item.IncomeAmt;
            expenseTotal += item.ExpenseAmt;
        });


    }, [dataItem]);


    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };


    const hasReadReportPermission = permissions.includes('Read Reports');

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            try {

                const response = await fetchData('EngineerExpenseApi', `/${fromDate}/${toDate}/${companyId}`, token);

                if (response && response.responseCode === '1111') {
                    setDataItem(response.response_data);
                } else {
                    console.error('Unexpected response:', response);
                }
            } catch (error) {
                console.error('Error fetching Packing Slip Details:', error);
            }
        }
    };

    // Usage example
    // Ensure to call fetchTableData after setting the dates
    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData();
        }
    }, [fromDate, toDate, hasReadReportPermission]);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };



    const groupByExpenseId = (dataItem) => {
        const grouped = dataItem.reduce((acc, item) => {
            acc[item.ExpenseID] = acc[item.ExpenseID] || {
                EngineerName: item.EngineerName,
                ExpenseEntryDate: item.ExpenseEntryDate,
                items: []
            };
            acc[item.ExpenseID].items.push({
                ExpenseTypeName: item.ExpenseTypeName,
                Note: item.Note,
                Amount: item.Amount,
                IsApproved: item.IsApproved,
                IsRejected: item.IsRejected
            });
            return acc;
        }, {});
        return Object.values(grouped);
    };

    const groupedData = groupByExpenseId(filteredDataItem);

    const handlePrint = () => {
        // Opening a new window
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Failed to open new window. Please allow pop-ups for this website.');
            return;
        }

        // Styling and HTML setup
        const style = `
            <style>
                body, table { font-family: Times New Roman; font-size: 16px; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #ddd; padding: 8px; }
                th { background-color: #f3f3f3; font-size: 19px; font-weight: bold; }
                h3 { font-size: 32px; }
            </style>
        `;

        const header = '<html><head><title>Engineer Expense</title>' + style + '</head><body>';
        const footer = '</body></html>';

        let htmlContent = header;
        htmlContent += '<h3>Engineer Expense Summary</h3><table><thead><tr>';
        const headers = ['Sl.No', 'Engineer', 'ExpenseEntryDate', 'ExpenseName', 'Note', 'Amount', 'Claim Status'];
        headers.forEach(header => htmlContent += `<th>${header}</th>`);
        htmlContent += '</tr></thead><tbody>';

        // Adding rows
        if (Array.isArray(groupedData)) {
            groupedData.forEach((group, index) => {
                group.items.forEach((item, itemIndex) => {
                    htmlContent += '<tr>';
                    if (itemIndex === 0) { // Group headers only once
                        htmlContent += `<td rowspan="${group.items.length}">${index + 1}</td>`;
                        htmlContent += `<td rowspan="${group.items.length}">${group.EngineerName || ''}</td>`;
                        htmlContent += `<td rowspan="${group.items.length}">${group.ExpenseEntryDate ? new Date(group.ExpenseEntryDate).toLocaleDateString('en-GB') : ''}</td>`;
                    }
                    htmlContent += `<td>${item.ExpenseTypeName || ''}</td>`;
                    htmlContent += `<td>${item.Note || ''}</td>`;
                    htmlContent += `<td>${item.Amount || ''}</td>`;
                    htmlContent += `<td>${item.IsApproved && !item.IsRejected ? 'Approved' : item.IsRejected ? 'Rejected' : 'Pending'}</td>`;
                    htmlContent += '</tr>';
                });
            });
        }

        htmlContent += '</tbody></table>' + footer;

        // Write to the new window and print
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
    };

    //excel

    const handleExportExcel = () => {
        const groupedData = groupByExpenseId(dataItem);  // Use the existing grouping function

        const excelData = [];
        let slNo = 1; // Initialize serial number

        // Add header row for the main table
        const headerRow = [
            'Sl.No',
            'Engineer',
            'Expense Entry Date',
            'Expense ',
            'Note',
            'Amount',
            'Claim Status',
        ];
        excelData.push(headerRow);

        // Process grouped data
        groupedData.forEach(group => {
            // Add a sub-header or some identifier for each group
            excelData.push([
                `${slNo++}`,
                `${group.EngineerName}`,
                `${new Date(group.ExpenseEntryDate).toLocaleDateString()}`
            ]);

            // Add rows for each item within the group
            group.items.forEach(item => {
                const claimStatus = item.IsApproved ? 'Approved' : item.IsRejected ? 'Rejected' : 'Pending';
                const dataRow = [
                    '',
                    '',  // Empty because Engineer name is on the group identifier
                    '',  // Empty because Date is on the group identifier
                    item.ExpenseTypeName,
                    item.Note,
                    item.Amount,
                    claimStatus,
                ];
                excelData.push(dataRow);
            });
        });

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(excelData);

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Expense Summary');

        // Save workbook to a file
        XLSX.writeFile(wb, 'ExpenseSummary.xlsx');
    };

    //Engineer Name
    const [selectedStaff, setSelectedStaff] = useState('');
    const [staffOptions, setStaffOptions] = useState([]);
    const [staffId, setStaffId] = useState(false);

    const fetchStaff = async () => {
        try {
            const response = await fetchData('StaffApi', `/${companyId}`);
            if (response.responseCode === '1111') {
                const options = response.response_data.map(staff => ({
                    // value: staff.firstName + " " + staff.lastName,
                    // label: staff.firstName + " " + staff.lastName,
                    value: staff.firstName,
                    label: staff.firstName,
                    staffId: staff.id
                }));
                setStaffOptions(options);
            }
        } catch (error) {
            console.error('Error fetching Staff:', error);
        }
    };

    const handleStaffChange = (selectedOption) => {
        setSelectedStaff(selectedOption);
    };


    useEffect(() => {
        fetchStaff();
    }, []);

    // const totalExpenseAmt = data.reduce((total, item) => {
    //     return total + (parseFloat(item.Amount) || 0);
    // }, 0);

    const totalExpenseAmt = dataItem.reduce((total, item) => {
        // Check if the claim is approved and not rejected before adding the amount to the total
        if (item.IsApproved && !item.IsRejected) {
            return total + (parseFloat(item.Amount) || 0);
        }
        return total; // Exclude amount for rejected claims
    }, 0);


    return (

        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>
            <div style={{ width: '100%', marginBottom: '2', display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                <div className='w-full mb-2 flex' style={{ marginTop: '10px' }}>
                    <h6 className="block uppercase tracking-wide text-black productHead flex-grow linkcolor" style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', color: 'black', fontWeight: 'bolder' }}>
                        ENGINEER EXPENSE SUMMARY
                    </h6>
                </div>

                <div style={{ marginBottom: '10px', marginLeft: '-20px', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                    {showSearchInput && (
                        <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ marginLeft: '10px', width: '200px' }}
                        />
                    )}
                </div>

                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        FromDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        ToDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <Button onClick={handleExportExcel} style={{ background: '#dc3545', color: 'whitesmoke', fontWeight: 'bolder', height: '25px', marginLeft: '5px', marginTop: '25px' }} disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Excel"}
                </Button>
            </div>
            <div className='w-full mb-5 flex' style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#090040' }}>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', padding: '5px', textTransform: 'uppercase' }}>Sl.No</TableCell>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', padding: '5px', textTransform: 'uppercase' }}>Engineer</TableCell>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', padding: '5px', textTransform: 'uppercase' }}>Expense Entry Date</TableCell>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', padding: '5px', textTransform: 'uppercase' }}>Expense</TableCell>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', padding: '5px', textTransform: 'uppercase' }}>Note</TableCell>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', padding: '5px', textTransform: 'uppercase' }}>Claim Status</TableCell>
                                {/* <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: '16px', fontWeight: 'bolder', color: 'black', padding: '7px' }}>Amount</TableCell> */}
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', textAlign: 'right', padding: '5px', textTransform: 'uppercase' }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody sx={{ backgroundColor: '#EAECEE' }}>
                            {groupedData.length > 0 ? (
                                groupedData.map((group, index) => (
                                    <React.Fragment key={group.id || index}>  {/* Use a unique ID if available */}
                                        {group.items.map((item, itemIndex) => (
                                            <TableRow key={item.id || itemIndex} className="customTableRows">  {/* Use a unique ID if available */}
                                                {itemIndex === 0 && (
                                                    <>
                                                        <TableCell rowSpan={group.items.length} sx={{ padding: '10px' }}>
                                                            {index + 1}
                                                        </TableCell>
                                                        <TableCell rowSpan={group.items.length} sx={{ padding: '10px' }}>
                                                            {group.EngineerName}
                                                        </TableCell>
                                                        <TableCell rowSpan={group.items.length} sx={{ padding: '10px' }}>
                                                            {group.ExpenseEntryDate &&
                                                                new Date(group.ExpenseEntryDate).toLocaleDateString('en-GB')}
                                                        </TableCell>
                                                    </>
                                                )}
                                                <TableCell sx={{ padding: '10px' }}>{item.ExpenseTypeName}</TableCell>
                                                <TableCell sx={{ padding: '10px' }}>{item.Note}</TableCell>

                                                {/* <TableCell sx={{ padding: '10px' }}>
                                                    {item.IsApproved && !item.IsRejected && "Approved Claim"}
                                                    {!item.IsApproved && item.IsRejected && "Rejected Claim"}
                                                    {!item.IsApproved && !item.IsRejected && "Pending Claim"}
                                                </TableCell> */}
                                                <TableCell sx={{
                                                    padding: '10px', color:
                                                        item.IsApproved && !item.IsRejected ? 'green' :
                                                            !item.IsApproved && item.IsRejected ? 'red' :
                                                                'blue'
                                                }}>
                                                    {item.IsApproved && !item.IsRejected && "Approved Claim"}
                                                    {!item.IsApproved && item.IsRejected && "Rejected Claim"}
                                                    {!item.IsApproved && !item.IsRejected && "Pending Claim"}
                                                </TableCell>

                                                <TableCell sx={{ padding: '10px', textAlign: 'right' }}>
                                                    {item.Amount ? Number(item.Amount).toFixed(2) : '0.00'}
                                                </TableCell>

                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7}>No data available</TableCell>  {/* Assuming 7 columns, adjust as necessary */}
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell colSpan={6} />
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total Expense: {totalExpenseAmt.toFixed(2)}</TableCell>
                            </TableRow>
                        </TableBody>
                        -
                    </Table>
                </TableContainer>
            </div>
        </div >
    );
}

export default EngineerExpense;
